import React, { useMemo, SetStateAction, useState, useEffect } from 'react';

import {
  ChannelListMessenger,
  ChannelListMessengerProps,
  ChannelSearchProps,
  SearchResultItemProps,
  ChannelList as StreamChannelList,
} from 'stream-chat-react';
import { BrkfstChannelFilters, ChatFilterState, SetFilter, StreamChatClient } from 'shared/messaging/types';
import { Text } from 'shared/components/display';
import { TEXT_VARIANTS } from 'shared/styles/text';
import { MESSAGE_COPY } from 'shared/config/copy';
import ChannelListPreview from 'features/messages/components/molecules/ChannelListPreview';
import { BrkfstUser } from 'shared/typings/user';
import { ChannelSearchFunctionParams } from 'stream-chat-react/dist/components/ChannelSearch/hooks/useChannelSearch';
import { createSearchEmpty } from 'features/messages/util';
import SearchCustomResultItem from 'features/messages/components/atoms/SearchCustomResultItem';
import { Channel, StreamChat } from 'stream-chat';
import { UserType } from 'shared/typings/user/enums';

import ChannelFilter from 'features/messages/components/organisms/ChannelFilter';
import { AccountManager, BrkfstAccount } from 'shared/typings/account';

import axiosClient from 'shared/axiosClient';
import { API_ACCOUNT_MANAGERS_ROUTES } from 'shared/config/routes/api/apiAccountManagerRoutes';
import { API_ACCOUNT_ROUTES } from 'shared/config/routes/api/apiAccountRoutes';

interface Props {
  organizationId: number;
  currentUser: BrkfstUser;
  chatClient: StreamChat;
  activeChannel: Channel;
}

const ChannelList: React.FC<Props> = ({ organizationId, currentUser, chatClient, activeChannel }) => {
  // state used by streamchat to filter the channels that are loaded
  const initialFilterState: ChatFilterState = {
    filter: {
      // by default only show channels that the user is a member of
      // for non-superadmin, scope to the current organization
      members: { $in: [`${currentUser.id}`] },
      ...(!currentUser.isSuperadmin && organizationId ? { organizationId } : {}),
    },
    loading: true,
    allBrands: [],
    accountManagers: [],
    selectedAccountManager: undefined,
    selectedBrands: [],
    search: '',
  };

  const [filter, setFilterState] = useState<ChatFilterState>(initialFilterState);
  const isCreator = currentUser.type === UserType.CREATOR;
  // Searching for channels has no existing context of the already parsed channel list so need to make
  // sure that we still query for channels the user is a member of or they will have access to
  // channels they are not a part of
  const customSearchFunction = async (
    props: ChannelSearchFunctionParams,
    event: { target: { value: SetStateAction<string> } },
    client: StreamChatClient,
  ) => {
    const { setResults, setSearching, setQuery } = props;
    const searchString = event.target.value.toString();

    //Inputing an empty string will force the lifecycle of searching so we cannot prevent the search results
    //from appearing. Populate an empty search when the string is empty.
    if (searchString.trim() === '') {
      setQuery(searchString);
      setResults([]);
      return;
    }
    const filterWithSearch: BrkfstChannelFilters = {
      ...filter.filter,
      name: { $autocomplete: searchString },
    };
    setFilterState((prev) => ({
      ...prev,
      filter: {
        ...prev.filter,
        ...filterWithSearch,
      },
    }));

    setSearching(true);
    setQuery(searchString);
    let channels = await client.queryChannels(filterWithSearch);

    //Parse the  channels to not display searches that include organizations and creators searching for their
    //own names
    if (!currentUser.isSuperadmin && organizationId) {
      channels = channels.filter((channel) => {
        const userChannelName = channel?.data?.name?.split('-')[0].trim().toLowerCase();
        return userChannelName?.includes(searchString.toLowerCase());
      });
    } else {
      channels = channels.filter((channel) => {
        const brandChannelName = channel?.data?.name?.split('-')[1].trim().toLowerCase();
        return brandChannelName?.includes(searchString.toLowerCase());
      });
    }
    setResults(channels);
    setSearching(false);
  };
  // Props used internally by streamchat to be passed to ChannelSearch. Defines the functionality for searching and the
  // components used to render the results.
  const searchProps: Omit<ChannelSearchProps, 'setChannels'> = {
    popupResults: false,
    clearSearchOnClickOutside: false,
    searchFunction: (params, event) => {
      return customSearchFunction(params, event, chatClient);
    },
    SearchEmpty: () =>
      createSearchEmpty(isCreator ? MESSAGE_COPY.BRAND_SEARCH_EMPTY : MESSAGE_COPY.CREATOR_SEARCH_EMPTY),
    SearchResultItem: (props: SearchResultItemProps) => <SearchCustomResultItem {...props} />,
    placeholder: isCreator ? MESSAGE_COPY.CREATOR_SEARCH_PLACEHOLDER : MESSAGE_COPY.BRAND_SEARCH_PLACEHOLDER,
  };

  // overrides default behavior when new Message is received on Channel that is not being watched
  const onMessageNew = (setChannels, e) => {
    if (e.channel.organizationId === organizationId) {
      setChannels((channels) => {
        return [e.channel, ...channels];
      });
    }
  };

  const setFilterOptions = async () => {
    // load all account managers
    const accountManagerResponse = axiosClient.httpRequest<AccountManager[]>({
      url: API_ACCOUNT_MANAGERS_ROUTES.ACCOUNT_MANAGER,
      method: 'GET',
    });

    // and all accounts
    const brandResponse = axiosClient.httpRequest<Pick<BrkfstAccount, 'name' | 'id'>[]>({
      url: API_ACCOUNT_ROUTES.ALL_ACCOUNTS,
      method: 'GET',
    });
    const [accountManagers, brands] = await Promise.allSettled([accountManagerResponse, brandResponse]);

    setFilterState((prev) => ({
      ...prev,
      accountManagers:
        accountManagers.status === 'fulfilled'
          ? accountManagers.value.data.map((manager) => ({
              label: manager.name,
              value: manager,
              selected: false,
            }))
          : [],
      allBrands:
        brands.status === 'fulfilled'
          ? brands.value.data.map((account) => ({
              label: account.name,
              value: account,
              selected: false,
            }))
          : [],
      loading: false,
    }));
  };

  useEffect(() => {
    // load options for filters
    if (currentUser.isSuperadmin) setFilterOptions();
  }, [currentUser.isSuperadmin]);

  const channelRenderFilterFn = (channels: Array<Channel>): Array<Channel> => {
    // stream chat is still showing the actively selected channel even if its not in the filter, so we need to manually filter them here

    // @ts-ignore
    const cIds: string[] = filter.filter.id?.$in;
    if (!cIds) return channels;

    return channels.filter((channel) => {
      return channel.id ? cIds.includes(channel.id) : false;
    });
  };

  return (
    <StreamChannelList
      customActiveChannel={activeChannel?.id}
      Preview={ChannelListPreview}
      filters={filter.filter}
      List={(props) => (
        <InnerList {...props} setFilter={setFilterState} filter={filter} isSuperadmin={currentUser.isSuperadmin} />
      )}
      EmptyStateIndicator={() => (
        <Text variant={TEXT_VARIANTS.SUBHEADING_SM} className="str-chat__no-channels-heading">
          {MESSAGE_COPY.HEADING_NO_CHANNELS}
        </Text>
      )}
      onMessageNew={!currentUser.isSuperadmin && organizationId ? onMessageNew : undefined} // applies to new messages that appear after refreshing the page, and channelFilter has not changed
      allowNewMessagesFromUnfilteredChannels={false} // applies to new messages that appear after channelFilter changes (ex: switching organizations)
      showChannelSearch={!currentUser.isSuperadmin || isCreator} // only show search for non-superadmin since they cannot filter and creators
      additionalChannelSearchProps={searchProps}
      LoadingIndicator={React.memo(() => (
        <div></div>
      ))} //Override the default loading channel list to hide the skeletons that have styling conflict with channel search
      channelRenderFilterFn={channelRenderFilterFn}
    />
  );
};

const InnerList: React.FC<
  ChannelListMessengerProps & { setFilter: SetFilter; filter: ChatFilterState; isSuperadmin?: boolean }
> = ({ setFilter, filter, isSuperadmin, ...props }) => {
  return (
    <>
      {isSuperadmin && <ChannelFilter setFilter={setFilter} filter={filter} />}
      <ChannelListMessenger {...props} />
    </>
  );
};

export default ChannelList;
